/**
 * ValidationPanel Component
 * Admin-only view for Stream Monitor validation status.
 * 
 * Displays:
 * - Overview table with all stations validation status
 * - Detail view with 7-day trend for selected station
 * - Flags explanation and history
 * 
 * This is an internal audit tool - does not affect metrics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, AlertTriangle, CheckCircle, AlertCircle, XCircle,
  ChevronDown, ChevronUp, Info, RefreshCw, Calendar,
  TrendingUp, TrendingDown, Minus, Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { getBackendUrl } from '../../utils/api';
import { toast } from 'sonner';

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    stable: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Stable' },
    watch: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle, label: 'Watch' },
    unstable: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: AlertTriangle, label: 'Unstable' },
    critical: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Critical' }
  };
  
  const { bg, text, icon: Icon, label } = config[status] || config.stable;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Score display with color coding
const ScoreDisplay = ({ score }) => {
  const getColor = (s) => {
    if (s >= 85) return 'text-green-400';
    if (s >= 70) return 'text-orange-400';
    if (s >= 55) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  return (
    <span className={`font-bold ${getColor(score)}`}>
      {score}
    </span>
  );
};

// Flags display
const FlagsDisplay = ({ flags }) => {
  if (!flags) return <span className="text-gray-500">—</span>;
  
  const activeFlags = Object.entries(flags).filter(([, v]) => v);
  
  if (activeFlags.length === 0) {
    return <span className="text-green-400 text-xs">✓ None</span>;
  }
  
  const flagLabels = {
    low_data_integrity: { label: 'Data', color: 'bg-red-500/30 text-red-300' },
    retention_anomaly: { label: 'Retention', color: 'bg-purple-500/30 text-purple-300' },
    listener_drift: { label: 'Listeners', color: 'bg-blue-500/30 text-blue-300' },
    metadata_issue: { label: 'Metadata', color: 'bg-orange-500/30 text-orange-300' },
    polling_unstable: { label: 'Polling', color: 'bg-yellow-500/30 text-yellow-300' }
  };
  
  return (
    <div className="flex flex-wrap gap-1">
      {activeFlags.map(([flag]) => {
        const { label, color } = flagLabels[flag] || { label: flag, color: 'bg-gray-500/30 text-gray-300' };
        return (
          <span key={flag} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${color}`}>
            {label}
          </span>
        );
      })}
    </div>
  );
};

// Trend indicator
const TrendIndicator = ({ trend }) => {
  if (trend === 'improving') {
    return <span className="text-green-400 flex items-center gap-1 text-xs"><TrendingUp className="w-3 h-3" /> Improving</span>;
  }
  if (trend === 'declining') {
    return <span className="text-red-400 flex items-center gap-1 text-xs"><TrendingDown className="w-3 h-3" /> Declining</span>;
  }
  return <span className="text-gray-400 flex items-center gap-1 text-xs"><Minus className="w-3 h-3" /> Stable</span>;
};

// Station detail panel
const StationDetailPanel = ({ stationId, stationName, token, onClose }) => {
  const [history, setHistory] = useState([]);
  const [trend, setTrend] = useState('stable');
  const [loading, setLoading] = useState(true);
  const backendUrl = getBackendUrl();
  
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(
          `${backendUrl}/api/stream-monitor/validation/station/${stationId}?days=7`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHistory(data.history || []);
          setTrend(data.trend || 'stable');
        }
      } catch (error) {
        console.error('Error fetching validation history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [stationId, backendUrl, token]);
  
  const latestValidation = history[0];
  
  return (
    <div className="bg-gray-800/50 rounded-lg p-4 mt-4 border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-purple-400" />
          <h3 className="font-semibold text-white">{stationName}</h3>
          <TrendIndicator trend={trend} />
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ChevronUp className="w-4 h-4" />
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-8 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : (
        <div className="space-y-4">
          {/* Latest metrics snapshot */}
          {latestValidation?.metrics_snapshot && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-black/30 rounded p-3">
                <div className="text-xs text-gray-400 mb-1">Total Songs</div>
                <div className="text-lg font-bold text-white">
                  {latestValidation.metrics_snapshot.total_songs}
                </div>
              </div>
              <div className="bg-black/30 rounded p-3">
                <div className="text-xs text-gray-400 mb-1">Valid Ratio</div>
                <div className="text-lg font-bold text-white">
                  {(latestValidation.metrics_snapshot.valid_ratio * 100).toFixed(1)}%
                </div>
              </div>
              <div className="bg-black/30 rounded p-3">
                <div className="text-xs text-gray-400 mb-1">Avg Retention</div>
                <div className="text-lg font-bold text-white">
                  {latestValidation.metrics_snapshot.avg_retention}%
                </div>
              </div>
              <div className="bg-black/30 rounded p-3">
                <div className="text-xs text-gray-400 mb-1">Fallback Ratio</div>
                <div className="text-lg font-bold text-white">
                  {(latestValidation.metrics_snapshot.fallback_ratio * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          
          {/* 7-day history table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 text-gray-400 font-medium">Date</th>
                  <th className="text-center py-2 text-gray-400 font-medium">Score</th>
                  <th className="text-center py-2 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-2 text-gray-400 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 text-gray-300">{h.date}</td>
                    <td className="py-2 text-center">
                      <ScoreDisplay score={h.validation_score} />
                    </td>
                    <td className="py-2 text-center">
                      <StatusBadge status={h.validation_status} />
                    </td>
                    <td className="py-2">
                      <FlagsDisplay flags={h.flags} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Penalties breakdown */}
          {latestValidation?.penalties_applied && (
            <div className="bg-black/30 rounded p-3">
              <div className="text-xs text-gray-400 mb-2">Penalties Applied (Latest)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(latestValidation.penalties_applied).map(([penalty, value]) => (
                  value > 0 && (
                    <span key={penalty} className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded">
                      {penalty.replace(/_/g, ' ')}: -{value}
                    </span>
                  )
                ))}
                {Object.values(latestValidation.penalties_applied).every(v => v === 0) && (
                  <span className="text-xs text-green-400">No penalties</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main ValidationPanel component
const ValidationPanel = ({ token }) => {
  const [validations, setValidations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const backendUrl = getBackendUrl();
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [validationsRes, summaryRes] = await Promise.all([
        fetch(`${backendUrl}/api/stream-monitor/validation/stations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/stream-monitor/validation/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (validationsRes.ok) {
        const data = await validationsRes.json();
        setValidations(data.validations || []);
      }
      
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching validation data:', error);
      toast.error('Σφάλμα φόρτωσης δεδομένων validation');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRunValidation = async () => {
    setRunning(true);
    try {
      const response = await fetch(`${backendUrl}/api/stream-monitor/validation/run`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Validation ολοκληρώθηκε: ${data.results?.stations_processed || 0} σταθμοί`);
        fetchData();
      } else {
        toast.error('Αποτυχία εκτέλεσης validation');
      }
    } catch (error) {
      console.error('Error running validation:', error);
      toast.error('Σφάλμα κατά την εκτέλεση validation');
    } finally {
      setRunning(false);
    }
  };
  
  return (
    <div className="bg-gray-900/50 rounded-xl p-4 sm:p-6 border border-white/10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-purple-400" />
          <div>
            <h2 className="text-xl font-bold text-white">Validation Panel</h2>
            <p className="text-sm text-gray-400">Internal monitoring audit (Admin only)</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowExplanation(!showExplanation)}
            className="border-white/20"
          >
            <Info className="w-4 h-4 mr-1" />
            Flags Info
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="border-white/20"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleRunValidation}
            disabled={running}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {running ? (
              <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Running...</>
            ) : (
              <><Activity className="w-4 h-4 mr-1" /> Run Now</>
            )}
          </Button>
        </div>
      </div>
      
      {/* Flags Explanation */}
      {showExplanation && (
        <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-white/10">
          <h3 className="font-semibold text-white mb-3">Validation Flags Explanation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-black/30 rounded p-3">
              <span className="text-red-300 font-medium">Low Data Integrity (-15)</span>
              <p className="text-gray-400 text-xs mt-1">Λιγότερο από 90% valid songs (artist/title/duration)</p>
            </div>
            <div className="bg-black/30 rounded p-3">
              <span className="text-purple-300 font-medium">Retention Anomaly (-20)</span>
              <p className="text-gray-400 text-xs mt-1">Retention διαφέρει περισσότερο από 2 std από εβδομαδιαίο μέσο</p>
            </div>
            <div className="bg-black/30 rounded p-3">
              <span className="text-blue-300 font-medium">Listener Drift (-15)</span>
              <p className="text-gray-400 text-xs mt-1">Max/Min listeners ratio πάνω από 3x (Live only)</p>
            </div>
            <div className="bg-black/30 rounded p-3">
              <span className="text-orange-300 font-medium">Metadata Issue (-10)</span>
              <p className="text-gray-400 text-xs mt-1">Πάνω από 20% fingerprint fallback</p>
            </div>
            <div className="bg-black/30 rounded p-3">
              <span className="text-yellow-300 font-medium">Polling Unstable (-20)</span>
              <p className="text-gray-400 text-xs mt-1">Πάνω από 5 gaps πάνω από 45s μεταξύ τραγουδιών</p>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            <strong>Monitoring Window:</strong> 06:00 - 21:00 Athens time | <strong>Score:</strong> 100 - penalties
          </div>
        </div>
      )}
      
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="bg-gray-800/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{summary.total_stations}</div>
            <div className="text-xs text-gray-400">Total</div>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3 text-center border border-green-500/20">
            <div className="text-2xl font-bold text-green-400">{summary.by_status?.stable || 0}</div>
            <div className="text-xs text-green-400/70">Stable</div>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-3 text-center border border-orange-500/20">
            <div className="text-2xl font-bold text-orange-400">{summary.by_status?.watch || 0}</div>
            <div className="text-xs text-orange-400/70">Watch</div>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3 text-center border border-yellow-500/20">
            <div className="text-2xl font-bold text-yellow-400">{summary.by_status?.unstable || 0}</div>
            <div className="text-xs text-yellow-400/70">Unstable</div>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/20">
            <div className="text-2xl font-bold text-red-400">{summary.by_status?.critical || 0}</div>
            <div className="text-xs text-red-400/70">Critical</div>
          </div>
        </div>
      )}
      
      {/* Stations Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" />
          Φόρτωση δεδομένων validation...
        </div>
      ) : validations.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Δεν υπάρχουν δεδομένα validation</p>
          <p className="text-sm mt-2">Πατήστε "Run Now" για να εκτελέσετε validation</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-gray-400 font-medium">Station</th>
                <th className="text-center py-3 px-2 text-gray-400 font-medium">Mode</th>
                <th className="text-center py-3 px-2 text-gray-400 font-medium">Score</th>
                <th className="text-center py-3 px-2 text-gray-400 font-medium">Status</th>
                <th className="text-left py-3 px-2 text-gray-400 font-medium">Flags</th>
                <th className="text-center py-3 px-2 text-gray-400 font-medium">Date</th>
                <th className="text-center py-3 px-2 text-gray-400 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {validations.map((v) => (
                <React.Fragment key={v.station_id}>
                  <tr 
                    className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors
                      ${selectedStation === v.station_id ? 'bg-white/10' : ''}`}
                    onClick={() => setSelectedStation(selectedStation === v.station_id ? null : v.station_id)}
                  >
                    <td className="py-3 px-2 text-white font-medium">{v.station_name}</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        v.data_mode === 'live' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {v.data_mode === 'live' ? 'Live' : 'Stream'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <ScoreDisplay score={v.validation_score} />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <StatusBadge status={v.validation_status} />
                    </td>
                    <td className="py-3 px-2">
                      <FlagsDisplay flags={v.flags} />
                    </td>
                    <td className="py-3 px-2 text-center text-gray-400 text-xs">{v.date}</td>
                    <td className="py-3 px-2 text-center">
                      {selectedStation === v.station_id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                  </tr>
                  {selectedStation === v.station_id && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <StationDetailPanel
                          stationId={v.station_id}
                          stationName={v.station_name}
                          token={token}
                          onClose={() => setSelectedStation(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Footer info */}
      <div className="mt-6 pt-4 border-t border-white/10 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {summary?.date && `Data for: ${summary.date}`}
          {summary?.avg_score !== undefined && ` | Average Score: ${summary.avg_score}`}
        </div>
        <div className="mt-1">
          Validation runs automatically at 03:30 Athens time for previous day's data (06:00-21:00 window).
        </div>
      </div>
    </div>
  );
};

export default ValidationPanel;
